/**
 * THE PROMISES. What was promised and not kept, what was done about it, and the two defects on the
 * schedule that are not promises but belong beside them. Office only.
 *
 * Six promises, from `docs/architecture.md` section 5, each a clock the nightly run checks. A
 * breach CARRIES THE REMEDY: the published policy says the choice between a free re-clean and a
 * full refund is the client's, so recording a remedy means saying whose choice it was, and the
 * server refuses one without the other. "Sanctioned" is a real state, for the case where a
 * published rule explains the difference, and naming it keeps the contradiction visible.
 *
 * Below the breaches, two lists that are CLAUDE.md binding rule 7 made visible: changes nobody has
 * resolved, meaning neither told nor a typed reason after a day, and standing schedules whose
 * written-ahead visits have run out. Both are a client with nobody noticing, so they are ONE table
 * with TWO GROUPS rather than two lists that scroll apart: the question either one answers is the
 * same question, which household is nobody watching.
 *
 * ON A DESK THE BREACHES ARE A TABLE AND THE WORK IS A PANEL. A phone opens the remedy under the
 * card it belongs to, because a sheet over the row hides the thing it is about; a desk has room to
 * hold the row and the form side by side, so the panel is the same form and the cards are not
 * drawn at all. Neither shape is a second list: both come from the one array the server sent.
 *
 * OPEN COMES FIRST, ALWAYS. A breach put right, waived or sanctioned is finished business kept for
 * the record, and it is never what somebody opened this screen to find.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CHOICE_OWNERS,
  REMEDIES,
  VISIT_CHANGE_KIND_LABELS,
  type BreachState,
  type ChoiceOwner,
  type Remedy,
} from '@fk/shared';
import {
  ApiError,
  listBreaches,
  patchBreach,
  type BreachList,
  type BreachRow,
  type StaleSeries,
  type UntoldChange,
} from '../api.ts';
import { Card, Detail, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import {
  DataTable,
  PanelRule,
  SidePanel,
  SplitLayout,
  useWideView,
  type DataColumn,
  type RowGroup,
} from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { GapPill, NotToldPill } from '../components/StatusPills.tsx';
import { formatDay, formatInstant, lateByWords, shortDay } from '../format.ts';
import {
  BREACH_STATE_LABELS,
  CHOICE_OWNER_LABELS,
  PROMISE_CODE_LABELS,
  REMEDY_LABELS,
} from '../labels.ts';

/**
 * The two defects, in one row type so one table can hold both. They are different rows about the
 * same failure, and a reader looking for "what is nobody watching" should not have to look twice.
 */
type Defect =
  | { readonly shape: 'change'; readonly id: string; readonly change: UntoldChange }
  | { readonly shape: 'series'; readonly id: string; readonly series: StaleSeries };

export function Promises() {
  const wide = useWideView();
  const [list, setList] = useState<BreachList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    listBreaches('')
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The promises did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  const changed = (sentence: string) => {
    setSaid(sentence);
    reload();
  };

  const breaches = list?.breaches ?? [];
  const open = breaches.filter((breach) => breach.state === 'open');
  const answered = breaches.filter((breach) => breach.state !== 'open');
  const breachGroups: readonly RowGroup<BreachRow>[] = [
    { key: 'open', caption: 'Nobody has put these right', rows: open },
    { key: 'answered', caption: 'Put right, waived or sanctioned', rows: answered },
  ].filter((group) => group.rows.length > 0);

  const defectGroups: readonly RowGroup<Defect>[] = [
    {
      key: 'changes',
      caption: 'Changes nobody has resolved',
      rows: (list?.untoldChanges ?? []).map((change): Defect => ({
        shape: 'change',
        id: change.id,
        change,
      })),
    },
    {
      key: 'series',
      caption: 'Standing schedules that stopped writing ahead',
      rows: (list?.staleSeries ?? []).map((series): Defect => ({
        shape: 'series',
        id: series.id,
        series,
      })),
    },
  ].filter((group) => group.rows.length > 0);

  const picked = breaches.find((breach) => breach.id === pickedId) ?? null;
  const panel =
    wide && picked ? (
      <SidePanel
        key={picked.id}
        title={PROMISE_CODE_LABELS[picked.code]}
        pills={<BreachStatePill state={picked.state} />}
        onClose={() => setPickedId(null)}
      >
        <BreachFacts breach={picked} />
        {picked.state === 'open' ? (
          <>
            <PanelRule />
            <h3 className="font-serif text-lg leading-7">Put this right</h3>
            <PutRight key={picked.id} idPrefix="panel" breach={picked} onChanged={changed} />
          </>
        ) : null}
      </SidePanel>
    ) : undefined;

  return (
    <div>
      <ScreenTitle title="Promises" subtitle="What was promised, and whether it was kept." />

      <p className="mb-4 text-sm">
        <Link
          to="/issues"
          className="inline-flex min-h-11 desk:min-h-9 items-center font-medium underline"
        >
          Issues: what went wrong at a house
        </Link>
      </p>

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {error ? (
        <ErrorNotice title="The promises did not load" message={error.message} onRetry={reload} />
      ) : list === null ? (
        <Busy what="the promises" />
      ) : (
        <SplitLayout panel={panel}>
          <section className="mb-6">
            <h2 className="mb-2 font-serif text-xl">Broken promises</h2>
            {breaches.length === 0 ? (
              <EmptyNotice title="Every promise kept">
                <p>The nightly check found nothing to report. This is the screen we want empty.</p>
              </EmptyNotice>
            ) : (
              <>
                <p className={`mb-2 hidden text-xs text-navy/60 md:block ${MEASURE}`}>
                  Open first. Pick a promise to put it right in the panel it opens.
                </p>
                <DataTable
                  label="Broken promises"
                  groups={breachGroups}
                  columns={breachColumns(setPickedId)}
                  rowKey={(row) => row.id}
                  rowClassName={(row) =>
                    row.id === pickedId
                      ? 'bg-gold/10 [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-gold [&>td:first-child]:pl-[13px]'
                      : ''
                  }
                  card={(row) => <BreachCard breach={row} onChanged={changed} actions={!wide} />}
                />
                {/* A CAPPED LIST SAYS SO, the way the calls, the follow-ups and the spend do. The
                    door answers the newest two hundred and no more, and a screen whose whole job
                    is what was promised and not kept must never quietly leave a breach off the
                    end of it. */}
                {breaches.length >= list.limit ? (
                  <p className={`mt-2 text-xs text-navy/60 ${MEASURE}`}>
                    The newest {list.limit} are shown, which is the most one read answers with.
                    There are more than this, and the oldest of them are not on this screen.
                  </p>
                ) : null}
              </>
            )}
          </section>

          <section className="mb-6">
            <h2 className="mb-2 font-serif text-xl">On the schedule</h2>
            <p className={`mb-2 text-sm text-navy/70 ${MEASURE}`}>
              Two things binding rule 7 makes visible. A change recorded a day or more ago that was
              neither told to the client nor explained, so the visit has not moved and will not
              until one of those is true. And a live arrangement whose visits are written less than
              two weeks ahead, which means the next visit is on nobody's calendar.
            </p>
            {defectGroups.length === 0 ? (
              <EmptyNotice title="Nothing on this list">
                <p>
                  Every change on the calendar has been told or explained, and every live
                  arrangement has its visits written ahead.
                </p>
              </EmptyNotice>
            ) : (
              <DataTable
                label="Defects on the schedule"
                groups={defectGroups}
                columns={DEFECT_COLUMNS}
                rowKey={(row) => `${row.shape}-${row.id}`}
                card={(row) => <DefectCard defect={row} />}
              />
            )}
          </section>
        </SplitLayout>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The tables, at md and above. The same rows the cards are drawn from.
 * ------------------------------------------------------------------ */

/**
 * The first column is a button rather than a link: it opens the panel beside the list, the way the
 * artboard works a row. The links out of a breach live in the panel, where each is 44px of target
 * instead of a line of 12px text in a table cell.
 *
 * THE WIDTHS ARE SHARES, NOT PIXELS. A fixed table layout takes the greater of its own width and
 * the sum of its columns, so 164 plus 172 plus 150 is 486px of table inside the 308px track the
 * panel left at 1024, and the wrapper clipped instead of scrolling: the coral "Open" pill went off
 * the right edge with nothing to scroll it back. A share always resolves against the track, so the
 * table is as wide as the room it has, and since ops #56 it scrolls in its own frame below the
 * floor `tableMinWidth` sets. The numbers are the artboard's proportions.
 */
function breachColumns(pick: (id: string) => void): readonly DataColumn<BreachRow>[] {
  return [
    {
      key: 'promise',
      header: 'Promise',
      cell: (row) => (
        <button
          type="button"
          onClick={() => pick(row.id)}
          className="flex min-h-11 desk:min-h-9 w-full items-center text-left font-medium"
        >
          {PROMISE_CODE_LABELS[row.code]}
        </button>
      ),
    },
    {
      key: 'household',
      header: 'Household',
      width: '23%',
      cell: (row) => (
        <>
          <span className="block text-navy/75">{row.clientName ?? 'No household on this one'}</span>
          {row.visitDate ? (
            <span className="block text-xs text-navy/60">Visit {shortDay(row.visitDate)}</span>
          ) : null}
        </>
      ),
    },
    {
      key: 'due',
      header: 'Was due',
      width: '24%',
      cell: (row) => (
        <>
          <span className="block text-navy/75">{formatInstant(row.dueAt)}</span>
          {lateByWords(row.missedByMinutes) ? (
            <span className="block text-xs text-navy/60">{lateByWords(row.missedByMinutes)}</span>
          ) : null}
        </>
      ),
    },
    {
      key: 'state',
      header: 'State',
      width: '20%',
      cell: (row) => <BreachStatePill state={row.state} />,
    },
  ];
}

const DEFECT_COLUMNS: readonly DataColumn<Defect>[] = [
  {
    key: 'what',
    header: 'What',
    width: '29%',
    cell: (row) =>
      row.shape === 'change' ? (
        <Link
          to={`/visits/${row.change.visitId}`}
          className="flex min-h-11 desk:min-h-9 items-center font-medium"
        >
          {VISIT_CHANGE_KIND_LABELS[row.change.kind]}
        </Link>
      ) : (
        <Link
          to={`/agreements/${row.series.agreementId}`}
          className="flex min-h-11 desk:min-h-9 items-center font-medium"
        >
          The visits have run out
        </Link>
      ),
  },
  {
    key: 'detail',
    header: 'The detail',
    cellClassName: 'text-navy/75',
    cell: (row) =>
      row.shape === 'change' ? (
        <>
          <span className="block font-serif text-[15px] leading-5 text-navy">
            {row.change.clientName}
          </span>
          <span className="block text-xs leading-4">{changeWords(row.change)}</span>
        </>
      ) : (
        seriesWords(row.series)
      ),
  },
  {
    key: 'missing',
    header: 'What is missing',
    width: '26%',
    cell: (row) =>
      row.shape === 'change' ? <NotToldPill /> : <GapPill>Nothing written ahead</GapPill>,
  },
];

/** Coral is for the open ones only. A breach somebody answered is a record, not a gap. */
function BreachStatePill({ state }: { state: BreachState }) {
  if (state === 'open') return <GapPill>Open</GapPill>;
  return <Pill tone={state === 'remedied' ? 'sage' : 'quiet'}>{BREACH_STATE_LABELS[state]}</Pill>;
}

/**
 * The change in a sentence, under the household's name. The town and the day the visit still
 * stands on come first, because "which of ours is this, and when" is the question a reader asks
 * before any of the rest of it.
 */
function changeWords(change: UntoldChange): string {
  const moved = [
    change.fromDate ? `From ${shortDay(change.fromDate)}` : null,
    change.toDate ? `to ${shortDay(change.toDate)}` : null,
  ]
    .filter(Boolean)
    .join(' ');
  return [
    `${change.town}, visit ${shortDay(change.scheduledDate)}`,
    moved || null,
    `Recorded ${formatInstant(change.createdAt) ?? 'at a moment nobody stamped'}`,
  ]
    .filter(Boolean)
    .join('. ')
    .concat('.');
}

function seriesWords(series: StaleSeries): string {
  const anchored = formatDay(series.anchorDate) ?? series.anchorDate;
  const ahead = series.materialisedTo ? formatDay(series.materialisedTo) : null;
  return `Anchored on ${anchored}, written ahead to ${ahead ?? 'nothing yet'}.`;
}

/* ------------------------------------------------------------------ *
 * The cards, below md, and the facts both shapes show.
 * ------------------------------------------------------------------ */

function BreachCard({
  breach,
  onChanged,
  actions,
}: {
  breach: BreachRow;
  onChanged: (sentence: string) => void;
  /**
   * False when the panel beside the table is drawing the same form. The card list is display:none
   * from md up rather than unmounted, so a card that kept its form would leave a second copy of
   * every control in the document, and a label resolves to the FIRST id it matches: pressing
   * "Waive it, because" in the panel would land in a control nobody can see.
   */
  actions: boolean;
}) {
  const open = breach.state === 'open';
  return (
    <Card className={open ? 'border-coral/40' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-serif text-lg leading-tight">{PROMISE_CODE_LABELS[breach.code]}</h3>
        <BreachStatePill state={breach.state} />
      </div>
      <BreachFacts breach={breach} />
      {actions && open ? (
        <details className="mt-3 rounded-lg border border-navy/15 p-3">
          <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
            Put this right
          </summary>
          <PutRight key={breach.id} idPrefix="card" breach={breach} onChanged={onChanged} />
        </details>
      ) : null}
    </Card>
  );
}

function BreachFacts({ breach }: { breach: BreachRow }) {
  return (
    <div>
      {breach.detail ? <p className="mt-2 text-sm">{breach.detail}</p> : null}
      <p className="mt-1 text-sm text-navy/75">
        {breach.clientName ?? 'No household on this one'}
        {breach.visitDate ? `, visit on ${shortDay(breach.visitDate)}` : ''}
        {lateByWords(breach.missedByMinutes) ? `, ${lateByWords(breach.missedByMinutes)}` : ''}.
      </p>
      {breach.visitId ? (
        <Link
          to={`/visits/${breach.visitId}`}
          className="inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium underline"
        >
          Open the visit
        </Link>
      ) : null}
      <dl className="mt-1">
        <Detail label="Found" value={formatInstant(breach.detectedAt)} />
        <Detail label="Was due" value={formatInstant(breach.dueAt)} />
        <Detail
          label="Remedy"
          value={
            breach.remedy
              ? `${REMEDY_LABELS[breach.remedy]}${
                  breach.choiceBelongsTo ? `. ${CHOICE_OWNER_LABELS[breach.choiceBelongsTo]}.` : ''
                }`
              : null
          }
        />
        <Detail label="Note" value={breach.remedyNote} />
        <Detail label="Put right" value={formatInstant(breach.remediedAt)} />
        <Detail label="Waived because" value={breach.waivedReason} />
        <Detail label="Sanctioned by" value={breach.sanctionedReason} />
      </dl>
    </div>
  );
}

/**
 * The form, in one definition. The card wraps it in a `details` on a phone and the panel draws it
 * open on a desk, so a rule about waivers is written once and cannot drift between the two. Each
 * copy names its controls after where it is drawn, so a label can only ever find its own.
 */
function PutRight({
  idPrefix,
  breach,
  onChanged,
}: {
  /** Where this copy is drawn, because the card and the panel can both be in the document. */
  idPrefix: string;
  breach: BreachRow;
  onChanged: (sentence: string) => void;
}) {
  const field = (name: string) => `${idPrefix}-breach-${name}-${breach.id}`;
  const [remedy, setRemedy] = useState<Remedy | ''>(breach.remedy ?? '');
  const [whose, setWhose] = useState<ChoiceOwner | ''>(breach.choiceBelongsTo ?? '');
  const [note, setNote] = useState(breach.remedyNote ?? '');
  const [waivedReason, setWaivedReason] = useState('');
  const [sanctionedReason, setSanctionedReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onChanged(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  function recordRemedy() {
    if (!remedy) {
      setProblem('Choose the remedy first.');
      return;
    }
    if (!whose) {
      setProblem("Say whose choice it was. The published policy says the choice is the client's.");
      return;
    }
    void run(
      () =>
        patchBreach(breach.id, {
          remedy,
          choiceBelongsTo: whose,
          remedyNote: note.trim() || null,
        }),
      'The remedy is on the record.',
    );
  }

  return (
    <div>
      {problem ? (
        <div className="mt-2">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <div className="mt-3">
        <SelectField
          id={field('remedy')}
          label="The remedy"
          value={remedy}
          onChange={setRemedy}
          options={REMEDIES}
          toLabel={(option) => REMEDY_LABELS[option]}
          placeholder="Choose one"
        />
        <SelectField
          id={field('whose')}
          label="Whose choice"
          value={whose}
          onChange={setWhose}
          options={CHOICE_OWNERS}
          toLabel={(option) => CHOICE_OWNER_LABELS[option]}
          placeholder="Say whose choice it was"
          hint="The published policy says the choice between a re-clean and a refund is the client's."
        />
        <TextAreaField id={field('note')} label="A note" value={note} onChange={setNote} rows={2} />
        <div className="flex flex-wrap gap-2">
          <Button tone="quiet" onClick={recordRemedy} disabled={busy}>
            Record the remedy
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              run(() => patchBreach(breach.id, { state: 'remedied' }), 'Put right, and stamped.')
            }
          >
            Mark it put right
          </Button>
        </div>
      </div>

      <div className="mt-4 border-t border-navy/10 pt-3">
        <TextField
          id={field('waive')}
          label="Waive it, because"
          value={waivedReason}
          onChange={setWaivedReason}
          autoComplete="off"
          hint="A real reason, on the record. The server refuses a waiver with none."
        />
        <Button
          tone="quiet"
          disabled={busy}
          onClick={() => {
            if (!waivedReason.trim()) {
              setProblem('Say why this breach is waived.');
              return;
            }
            void run(
              () => patchBreach(breach.id, { state: 'waived', waivedReason: waivedReason.trim() }),
              'Waived, with the reason on the record.',
            );
          }}
        >
          Waive it
        </Button>
      </div>

      <div className="mt-4 border-t border-navy/10 pt-3">
        <TextField
          id={field('sanction')}
          label="A published rule sanctions this, namely"
          value={sanctionedReason}
          onChange={setSanctionedReason}
          autoComplete="off"
          hint="Which published rule explains the difference. Naming it keeps a contradiction visible rather than muted."
        />
        <Button
          tone="quiet"
          disabled={busy}
          onClick={() => {
            if (!sanctionedReason.trim()) {
              setProblem('Say which published rule sanctions this difference.');
              return;
            }
            void run(
              () =>
                patchBreach(breach.id, {
                  state: 'sanctioned',
                  sanctionedReason: sanctionedReason.trim(),
                }),
              'Recorded as sanctioned by a published rule.',
            );
          }}
        >
          It is sanctioned
        </Button>
      </div>
    </div>
  );
}

function DefectCard({ defect }: { defect: Defect }) {
  if (defect.shape === 'change') {
    const change = defect.change;
    return (
      <Link to={`/visits/${change.visitId}`} className="block">
        <Card className="border-coral/40">
          <h3 className="font-serif text-lg leading-tight">{change.clientName}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-medium">{VISIT_CHANGE_KIND_LABELS[change.kind]}</span>
            <NotToldPill />
          </div>
          <p className="mt-1 text-sm text-navy/75">{changeWords(change)}</p>
        </Card>
      </Link>
    );
  }

  const series = defect.series;
  return (
    <Link to={`/agreements/${series.agreementId}`} className="block">
      <Card className="border-coral/40">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">The visits have run out</span>
          <GapPill>Nothing written ahead</GapPill>
        </div>
        <p className="mt-1 text-sm text-navy/75">{seriesWords(series)}</p>
        <p className="mt-1 text-xs text-navy/60">Open the arrangement</p>
      </Card>
    </Link>
  );
}
